import * as React from "react";

import moment from "moment";

import { AccuracyFilters, AccuracyResult, AccuracyRegionSetIdByState } from "@app/projects/network-assessment/accuracy";
import { AdequacyResult } from "@app/projects/network-assessment/adequacy";
import { AttestationResult } from "@app/projects/network-assessment/attestation";
import { CompositeFilters, CompositeResult } from "@app/projects/network-assessment/composite";
import { AccuracyTrendChartItem, AdequacyTrendChartItem, SpecialtyGroupException } from "@app/projects/results/common";
import { RegionSetQualifier } from "@app/region-sets";
import { AccuracyTemplate } from "@app/templates";
import { ProjectOptions, ProjectPerspective, ProjectRunConfigurationResult } from ".";
import { NetworkAssessmentAnalysisType, ProjectRunConfigurationType, RegionSetStatusType, TimeFrequency, AttestationRateDays } from "./enumerators";
//import { ComparedRecord } from "./network-assessment";
import RegionFilters from "./regionFilters";
import { AdequacySharedProjectContextProps, RegionFilterItem } from "./adequacySharedProjectContextProps";

/** The shape of possible query string parameters. */
export interface AdequacyQueryShape {
  all?: string;
  exceptions?: string;
  gaps?: string;
  changedGaps?: string;
  viewBy?: ProjectPerspective;
}

interface CountyProjectContextProps extends AdequacySharedProjectContextProps {
  accuracyFilters: AccuracyFilters;
  accuracyResult: AccuracyResult;
  accuracyTrendChartData: AccuracyTrendChartItem[];
  adequacyResult: AdequacyResult;
  adequacyTrendChartData: AdequacyTrendChartItem[];
  adequacyTrendChartDataDualMembership: AdequacyTrendChartItem[];
  attestationResult: AttestationResult;
  clearCachedImpactAnalysisRunConfigurations: () => void;
  clearFilters: () => void;
  cmsCreditIsApplying: boolean;
  cmsCreditShowOnlyChanges: boolean;
  cmsCreditTemplateValue: string;
  compareToAdequacyResult?: AdequacyResult;
  compareToAttestationResult?: AttestationResult;
  compareToCompositeResult?: CompositeResult;
  compositeResult: CompositeResult;
  countyRegionSetIds: AccuracyRegionSetIdByState[];
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;

  compositeFilters: CompositeFilters;
  compareToAccuracyResult?: AccuracyResult;
  compareToRunConfiguration?: ProjectRunConfigurationResult;
  compareToRunConfigurationTimestamp?: moment.Moment;
  filters: RegionFilterItem[];
  forceUpdateProject: () => Promise<void>;
  hasProspectOrMarketProviders: boolean;
  //getComparedViewByAllResults: (resultRegions: ResultRegion[]) => Promise<Array<ComparedRecord<AdequacyDetail>>>;
  /*
   * Set when opening a project from the home page.
   * If project is dual membership, this tells it to open in dual membership compare mode.
   * If project is not dual membership, this does nothing.
   */
  initialDualMembershipMode: boolean;
  loadedAccuracyTemplates: boolean;
  loadedFilters: boolean;
  loadingResults: boolean;

  regionFilters: RegionFilters;
  removeFilter: (urlPart: string) => void;
  requeueMarketAndProspectNCP: () => Promise<void>;
  rerunAccuracyGapAnalysis: (projectId: number, projectRunConfigurationId: number) => Promise<void>;
  // this is redeclared here (in addition to the shared interface) because it's a RunConfigurationResult here
  runConfiguration: ProjectRunConfigurationResult;
  runConfigurationTimestamp: moment.Moment;
  runProject: () => Promise<void>;
  setAttestationRateDays: (attestationDays: AttestationRateDays) => void;
  setBulkStatus: (regions: RegionSetQualifier[], newStatus: RegionSetStatusType) => void;
  setCMSCreditIsApplying: (active: boolean) => void;
  setCMSCreditShowOnlyChanges: (active: boolean) => void;
  setDualMembershipMode: (isDualMembershipMode: boolean, projectRunConfigurationType?: ProjectRunConfigurationType, primaryConfigurationId?: number, compareToRunConfigurationId?: number, forceLoading?: boolean) => void;
  setRunConfiguration: (runConfigurationId: number, runConfigurationTimestamp?: moment.Moment, isCompareTo?: boolean, runFrequency?: TimeFrequency, delayUpdate?: boolean) => Promise<Partial<CountyProjectContainerState>>;
  setException: (exception: SpecialtyGroupException) => Promise<void>;
  setLoading: (setLoading: boolean) => void | CountyProjectContainerState;
  setPerspective: (perspective: ProjectPerspective, forceRefresh: boolean) => void;
  setProjectHasUnprocessedTerminations: (needsToProcess: boolean, returnPartial?: boolean) => void | Partial<ProjectOptions>;
  setProjectProjectionFlags: (hasUnprocessedProviderTerminations: boolean, hasUnprocessedProjectionProviders: boolean) => void;
  setProjectReadOnlyProperties: (isReadOnly: boolean, isEditRestricted: boolean, delay?: boolean) => ProjectOptions;
  setRegionFilters: (regionFilters: RegionFilters, isCancelled: () => boolean) => void;
  setStatus: (region: RegionSetQualifier, newStatus: RegionSetStatusType) => void;
  updateRunConfigurationState: (newState: Partial<CountyProjectContainerState>) => void;
  updateRunConfigurationComment: (comment: string, runConfigurationId: number) => void;
}

type CountyProjectContainerState = CountyProjectContextProps & {
  accuracyTemplate?: AccuracyTemplate;
  loadedDefaultPerspective: boolean;
};

const CountyProjectContext = React.createContext<CountyProjectContextProps>(null);

function isComparedResultOnTop(countyProjectContextProps: CountyProjectContextProps): boolean {
  const runConfigurationDate = moment.utc(countyProjectContextProps.runConfigurationTimestamp);
  const compareToRunConfigurationDate = moment.utc(countyProjectContextProps.compareToRunConfigurationTimestamp);
  /** If true, the compared run configuration providers should be displayed on top. */
  const onTop = compareToRunConfigurationDate.isBefore(runConfigurationDate);
  return onTop;
}

function withCountyProjectContext<T>(Component: React.ComponentType<React.PropsWithChildren<T & CountyProjectContextProps>>): React.ComponentType<React.PropsWithChildren<T>> {
  class WithCountyProjectContext extends React.PureComponent<T> {
    public render() {
      return (
        <CountyProjectContext.Consumer>
          {(contextProps) => <Component {...this.props} {...contextProps} />}
        </CountyProjectContext.Consumer>
      );
    }
  }

  return WithCountyProjectContext;
}

export { isComparedResultOnTop, CountyProjectContainerState, CountyProjectContext, CountyProjectContextProps, RegionFilterItem, withCountyProjectContext };
